import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableColumnDefinition,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableRowData,
  TableSelectionCell,
  createTableColumn,
  useTableColumnSizing_unstable,
  useTableFeatures,
  useTableSelection,
} from "@fluentui/react-components";
import { Maybe } from "graphql/jsutils/Maybe";
import { LoadingBar } from "../Primitives/LoadingBar";
import { TFile, getPrettySize } from "../../models/file-model";
import { TStaffMember } from "../../models/staff-member-model";
import { FileIconPresentational } from "../FileIcon/FileIconPresentational";
import { StaffMemberPresentational } from "../StaffMember/StaffMemberPresentational";

type NameCell = {
  name: string;
  label: string;
  extension: string | null;
};

type SizeCell = {
  label: string;
  size: number;
};

type DateCreatedCell = {
  label: string;
  timestamp: number;
};

type DateModifiedCell = {
  label: string;
  timestamp: number;
};

type AuthorCell = {
  label: string | null;
  staffId: string | null;
  initials: string | null;
  fillColor: string | null;
  borderColor: string | null;
};

type Props = {
  files: TFile[];
  isLoadingFiles: boolean;
  selectedFileIds: string[];

  onSelectFile: (target: TFile) => void;
};

type TableItem = {
  itemId: string;
  item: NameCell;
  size: SizeCell;
  author: AuthorCell;
  isChecked: boolean;
  itemType: "FILE" | "FOLDER";
  dateCreated: DateCreatedCell;
  dateModified: DateModifiedCell;
};

const Author = (props: { item: TableItem }) => {
  const staffMember: TStaffMember = {
    fillColor: props.item.author.fillColor ?? "",
    strokeColor: props.item.author.borderColor ?? "",
    staffId: props.item.author.staffId ?? "",
    initials: props.item.author.initials ?? "",
    name: props.item.author.label ?? "",
  };

  return <StaffMemberPresentational staffMember={staffMember} initials={false} />;
};

const columnsDef: TableColumnDefinition<TableItem>[] = [
  createTableColumn<TableItem>({
    columnId: "check",
    renderHeaderCell: () => "",
  }),
  createTableColumn<TableItem>({
    columnId: "item",
    renderHeaderCell: () => "Name",
    renderCell: (item) => (
      <TableCellLayout className="block [&>div]:truncate">
        <div className="flex flex-1 flex-row min-w-0 max-w-full items-center">
          <FileIconPresentational className="h-5" extension={item.item.extension} />
          <div className="truncate overflow-hidden ml-2">{item.item.label}</div>
        </div>
      </TableCellLayout>
    ),
    compare: (a, b) => a.item.label.localeCompare(b.item.label),
  }),
  createTableColumn<TableItem>({
    columnId: "size",
    renderHeaderCell: () => "Size",
    renderCell: (item) => item.size.label,
    compare: (a, b) => (a.size.size === b.size.size ? 1 : 0),
  }),
  createTableColumn<TableItem>({
    columnId: "dateCreated",
    renderHeaderCell: () => "Date Created",
    renderCell: (item) => item.dateCreated.label,
    compare: (a, b) => a.dateCreated.timestamp - b.dateCreated.timestamp,
  }),
  createTableColumn<TableItem>({
    columnId: "dateModified",
    renderHeaderCell: () => "Date Modified",
    renderCell: (item) => item.dateModified.label,
    compare: (a, b) => a.dateModified.timestamp - b.dateModified.timestamp,
  }),
  createTableColumn<TableItem>({
    columnId: "staff",
    renderHeaderCell: () => "Staff",
    renderCell: (item) => (
      <TableCellLayout>
        <Author item={item} />
      </TableCellLayout>
    ),
    compare: (a, b) => a.author.staffId?.localeCompare(b.author.staffId || "") || 0,
  }),
];

const columnSizingOptions = {
  check: {
    width: 40,
    minWidth: 40,
    maxWidth: 40,
    idealWidth: 40,
  },
  item: {
    defaultWidth: 400,
    minWidth: 300,
    idealWidth: 500,
  },
  size: {
    width: 90,
    minWidth: 90,
    maxWidth: 90,
    idealWidth: 90,
  },
  dateCreated: {
    defaultWidth: 120,
    minWidth: 120,
    maxWidth: 120,
    idealWidth: 120,
  },
  dateModified: {
    defaultWidth: 120,
    minWidth: 120,
    maxWidth: 120,
    idealWidth: 120,
  },
  staff: {
    defaultWidth: 180,
    minWidth: 120,
    idealWidth: 180,
  },
};

const getMaybeString = (maybeString: Maybe<string>): string => maybeString ?? "";

export const FilesGridPresentational = (props: Props) => {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set(props.selectedFileIds || []));

  useEffect(() => {
    const containedSelected = (props.selectedFileIds || []).filter((id) =>
      (props.files || []).some((file) => file.id === id)
    );
    setSelectedItems(new Set(containedSelected));
  }, [props.files]);

  useEffect(() => {
    const selected = props.selectedFileIds || [];
    setSelectedItems(new Set(selected));
  }, [props.selectedFileIds]);

  const items = props.files.map<TableItem>((file) => {
    const name = getMaybeString(file.name);
    const extension = getMaybeString(file.fileExtension);

    const dateCreated = new Date(file.dateCreated || "");
    const dateModified = new Date(file.dateModified || "");

    return {
      itemType: file.type,
      isChecked: false,
      itemId: file.id,
      item: {
        name,
        extension,
        label: `${name}${extension}`,
      },
      size: { size: file.sizeBytes ?? 0, label: getPrettySize(file) },
      author: {
        initials: "initials",
        label: getMaybeString(file.author),
        staffId: getMaybeString(file.author),
        fillColor: getMaybeString(file.authorFill),
        borderColor: getMaybeString(file.authorStroke),
      },
      dateCreated: {
        timestamp: dateCreated.getTime(),
        label: dateCreated.toLocaleDateString(),
      },
      dateModified: {
        timestamp: dateModified.getTime(),
        label: dateModified.toLocaleDateString(),
      },
    };
  });

  const [columns] = React.useState<TableColumnDefinition<TableItem>[]>(columnsDef);

  const isRowSelected2 = (itemId: string): boolean => (itemId ? selectedItems.has(itemId) : false);

  const {
    getRows,
    columnSizing_unstable: columnSizing,
    tableRef,
    selection: { toggleRow },
  } = useTableFeatures({ columns, items }, [
    useTableColumnSizing_unstable({ columnSizingOptions, autoFitColumns: true }),
    useTableSelection({
      selectionMode: "multiselect",
      selectedItems: selectedItems,
    }),
  ]);

  const handleSelection = (e: React.SyntheticEvent, row: TableRowData<TableItem>): void => {
    if (!row) {
      return;
    }

    toggleFileSelection(e, row);
  };

  const toggleFileSelection = (e: React.SyntheticEvent, row: TableRowData<TableItem>): void => {
    if (!row) {
      return;
    }

    const file = props.files.find((f) => f.id === row.item.itemId);

    if (file) {
      toggleRow(e, row.rowId);
      props.onSelectFile(file);
    }
  };

  const rows = getRows((row) => {
    const selected = isRowSelected2(row.item?.itemId);

    return {
      ...row,
      onClick: (e: React.MouseEvent) => handleSelection(e, row),
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === " ") {
          e.preventDefault();
          handleSelection(e, row);
        }
      },
      selected,
      appearance: selected ? ("brand" as const) : ("none" as const),
    };
  });

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-y-auto scrollbar scrollbar-thumb-rounded-full scrollbar-thumb-neutral-250 hover:scrollbar-thumb-neutral-700 active:scrollbar-thumb-neutral-500 scrollbar-track-transparent">
      {props.isLoadingFiles && <LoadingBar barClassName="bg-neutral-400" />}
      <Table ref={tableRef} {...columnSizing.getTableProps()}>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHeaderCell key={column.columnId} {...columnSizing.getTableHeaderCellProps(column.columnId)}>
                <span className="font-semibold">{column.renderHeaderCell()}</span>
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ item, selected, onClick, onKeyDown, appearance }) => (
            <TableRow
              key={item.itemId}
              onClick={onClick}
              onKeyDown={onKeyDown}
              aria-selected={selected}
              appearance={appearance}
            >
              {columns.map((column) =>
                column.columnId === "check" ? (
                  <TableSelectionCell
                    checked={selected}
                    key={column.columnId}
                    {...columnSizing.getTableCellProps(column.columnId)}
                    checkboxIndicator={{ "aria-label": "Select row" }}
                  />
                ) : (
                  <TableCell key={column.columnId} {...columnSizing.getTableCellProps(column.columnId)}>
                    {column.renderCell(item)}
                  </TableCell>
                )
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
